// This are functions that only apply to WD so it's best to keep here
import constants from "toolConstants/warehouseDesigner/constants";
import initialState from "toolConstants/warehouseDesigner/initalState";
import { getAvailableArea } from "reduxModules/ducks/container";
import { parseDistance } from "utilities/format";
import { assign, differenceWith, find, flatten, intersectionBy, intersectionWith, isEqual, keys, some, values } from "lodash";
import equipmentCatalog from "globalConstants/EquipmentCatalog";

const { defMountingMin, defMountingMax, palletRackMinDim } = constants.area;

// Called from container, splits area setup depending on whether we're adding a dock area or warehouse area 
export const setArea = (container, area) => {
  switch (area.areaType) {
    case 'dock':
      return setDockArea(container, area);
    default:
      return setWarehouseArea(container, area);
  };
};

// Sets the next area to add based on the largest available space 
const setWarehouseArea = (container, area) => {
  const areaDims = getAvailableArea(container);
  assign(area, areaDims);
  const height = container.roofHeight.min;
  area.ceilingHeight = { min: height, max: height };
  area.mountingHeight = { min: height - defMountingMin, max: height - defMountingMax };
  return area;
};

// Sets initial dock area 
const setDockArea = (container, dock) => {
  // set dims first 
  const docks = container.loadingDocks;
  const travelLane = container.travelLane.width;
  const side = dock.id.split('_')[0];
  const lr = ['left', 'right'].includes(side);
  // Set dimensions and location
  dock.dimensions.length = parseDistance(lr ? travelLane : container.dimensions.length);
  dock.dimensions.width = parseDistance(lr ? container.dimensions.width - (docks.top_docks + docks.bottom_docks) * travelLane : travelLane);
  dock.location.left = parseDistance(side === 'right' ? container.dimensions.length - travelLane : 0);
  dock.location.top = parseDistance(lr ? docks.top_docks * travelLane : side === 'top' ? 0 : container.dimensions.width - travelLane);
  const height = container.roofHeight.min;
  dock.ceilingHeight = { min: height, max: height };
  dock.mountingHeight = { min: height - defMountingMin, max: height - defMountingMax };
  dock.storageReady = true;
  dock.areaUsage = 'trafficArea';
  dock.coverage = keys(dock.coverage).reduce((acc, key) => ({ ...acc, [key]: key === 'general' }), {});
  return dock
};




export const resetArea = (container, parameter = null) => {

  const activeArea = container.areas[container.activeTab];
  const initState = initialState.area();

  const customized = some(activeArea.customized)
  // get a list of keys and add the customized keys?
  // add the aisles with a function

  if (customized) {

    keys(activeArea.customized).forEach(key => activeArea[key] = initState[key]);

    activeArea.speakerLayout = initState.speakerLayout;
    activeArea.speakers = initState.speakers;
    activeArea.customized = initState.customized;

    if (parameter === 'storageOrientation') { // both true and paramter 
      activeArea.pickingAisles = initState.pickingAisles;
      activeArea.primaryAisles = initState.primaryAisles;
      activeArea.connectorAisles = initState.connectorAisles;
      // activeArea.aislesArray = initState.aislesArray;
    }
    // marker moved here to prevent error when area gets smaller and is customized
    activeArea.aislesArray = initState.aislesArray;
    activeArea.rackArrays = initState.rackArrays;

  } else {
    //the key list is fixed below
    if (values(activeArea.dimensions).some(x => x <= palletRackMinDim)) {
      activeArea.areaUsage = 'trafficArea';
    };
    const usage = activeArea.areaUsage;
    activeArea.coverage = {
      picking_aisles: usage === 'palletRack',
      primary_aisles: false,
      connector_aisles: false,
      general: usage !== 'palletRack',
      none: false,
    };

    activeArea.pickingAisles = initState.pickingAisles;
    activeArea.primaryAisles = initState.primaryAisles;
    activeArea.connectorAisles = initState.connectorAisles;
    activeArea.aislesArray = initState.aislesArray;
    activeArea.rackArrays = initState.rackArrays;
    activeArea.rackFill = initState.rackFill;

    activeArea.systemPrimaryUsage = initState.systemPrimaryUsage;
    activeArea.budget = initState.budget;
    activeArea.speakerAiming = initState.speakerAiming;
    activeArea.speakerLayout = initState.speakerLayout;
    activeArea.speakers = initState.speakers;
    activeArea.speakerModel = null;
    activeArea.customized = initState.customized;
  };
};

/**
 * @function calculateEquipmentTable
 * @description Generates a summary table of speaker equipment used across multiple areas.
 * Aggregates speakers by model, counting quantities and including product details from
 * the equipment catalog.
 * 
 * @param {Array<Object>} areas - Array of area objects containing speaker configurations
 * @param {Array<Object>} areas[].speakers - Array of speakers in each area
 * @returns {Array<Object>} Array of equipment summary objects
 * @property {number} returns[].quantity - Number of speakers of this model
 * @property {Object} returns[].product - Product information
 * @property {string} returns[].product.name - Model name of the speaker
 * @property {string} returns[].product.link - Link to product details on Biamp website
 * @property {string} returns[].description - Product description from equipment catalog
 * 
 * @example
 * const areas = [
 *   { speakers: [{ modelName: 'DS40' }, { modelName: 'DS40' }] },
 *   { speakers: [{ modelName: 'CM20' }] }
 * ];
 * const table = calculateEquipmentTable(areas);
 * // Returns [
 * //   { quantity: 2, product: { name: 'DS40', link: '...' }, description: '...' },
 * //   { quantity: 1, product: { name: 'CM20', link: '...' }, description: '...' }
 * // ]
 * 
 * @note
 * - Assumes equipmentCatalog.Speakers is available in scope
 * - Builds product URLs using Biamp's product catalog format
 * - Groups identical speaker models and maintains a count
 * - Links to official Biamp product pages for each model
 */
export const calculateEquipmentTable = (areas) => {
  let deviceList = areas.flatMap(area => area.speakers)

  /**
   * @description This section changes CCA-80 speakers for CCA-80 when:
   * a: speakers are facing oposite direction
   * b: speakers are closer that 1m 
   * This ensures product recomendation match the best solution when using the CCA-80 in a center-out alignment
   */
  // pre-process doubles 
  // Helper function to check if they are close together
  const isClose = (speaker1, speaker2) => {
    const dx = speaker2.location.x - speaker1.location.x;
    const dy = speaker2.location.y - speaker1.location.y;
    const dz = speaker2.location.z - speaker1.location.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz) <= 1;
  };

  // Only do this if there are CCA-80s
  if ( some(deviceList, device => device.modelName === 'CCA-80')) {
    const facingRight = deviceList.filter(device => device.modelName === 'CCA-80' && device.horAngle < 180);
    const facingLeft = deviceList.filter(device => device.modelName === 'CCA-80' && device.horAngle >= 180);

    // find pairs 
    // intersectionWith only returns facing right speakers =
    const matchedPairs = intersectionWith(facingRight, facingLeft, isClose);
    const pairs = matchedPairs.map(speaker => (
      [speaker, facingLeft.find(leftSPeaker => isClose(speaker, leftSPeaker))]
    ));

    // remove pairs from main array 
    deviceList = differenceWith(deviceList, flatten(pairs));

    // Reduce the pairs and add to list
    const newPairs = Array.from(pairs, pair => ({
      modelName: 'CCA-80D'
    }));
    deviceList = deviceList.concat(newPairs);
  };

  const tableData = deviceList.reduce((acc, curr) => {
    const prod = acc.find(x => x.product.name === curr.modelName);
    if (prod) {
      prod.quantity++;
    } else {
      const spkr = find(equipmentCatalog.Speakers, spkr => spkr.modelName === curr.modelName);
      acc.push({
        quantity: 1,
        product: { name: spkr.modelName, link: `https://products.biamp.com/product-details/-/o/ecom-item/${spkr.partNumber}` },
        description: spkr.description,
      })
    };
    return acc
  }, []);
  return tableData
};